'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes - local-first data is stable
        gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
        refetchOnWindowFocus: false, // Automerge handles reactivity
        retry: 1, // Only retry once to fail faster
        throwOnError: false, // Don't throw errors to error boundary by default
      },
      mutations: {
        retry: 0, // Don't retry mutations
        throwOnError: false, // Don't throw errors to error boundary by default
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: use singleton pattern to keep the same query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient()

      // Set up global error handler for queries
      browserQueryClient.getQueryCache().config.onError = (error) => {
        console.error('React Query error:', {
          error,
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        })
      }
    }
    return browserQueryClient
  }
}

const ReactQueryProvider = ({ children }: { children: React.ReactNode }) => {
  // Use useState to ensure this only runs on client and doesn't cause SSR issues
  const [queryClient] = useState(() => getQueryClient())

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}

export default ReactQueryProvider
